// src/staffRoles/index.js - promotions and acting roles on the staff form in admin (v133.52).
// Bundled by esbuild -> dist/staffRoles.js (globalName "StaffRoles"), loaded in admin.html, drawn into
// #sfRolesBox inside the Edit Staff modal (opened by Uploads.staff.openStaffModal).
//
// Matt, 6 Oct 2026: a promoted person's record carries on as the new role, with its history, and a
// temporary step up (acting) is possible between two dates. The rules are src/shared/role-periods.js
// (shared with the Worker); the Worker (src/worker/routes/staff-roles.js) allows the changes for Head
// Office only. This page only offers what the person may do.
import { PROMOTION_ROLES, roleLabel, describePeriod } from '../shared/role-periods.js';

let _staffId = null;
let _data = null;      // { ready, headOffice, today, periods, now, upcoming }
let _acting = {};      // staff_id -> { role, starts_on, ends_on, running } for the staff list chips

function authHeaders() {
  const t = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  const h = { 'Content-Type': 'application/json' };
  if (t) h['Authorization'] = 'Bearer ' + t;
  return h;
}
function workerBase() { return (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : ''; }
function toast(msg, kind) { if (typeof showToast === 'function') showToast(msg, kind); }
const esc = (s) => (window.escapeHtml ? window.escapeHtml(s) : String(s == null ? '' : s));
function areas() { try { return (typeof ALL_AREAS !== 'undefined' && Array.isArray(ALL_AREAS)) ? ALL_AREAS : []; } catch (e) { return []; } }
function areaName(id) { const a = areas().find((x) => String(x.id) === String(id)); return a ? a.name : String(id || ''); }
function shortDate(d) {
  try { return new Date(d + 'T00:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }); } catch (e) { return d; }
}
async function call(path, opts) {
  const res = await fetch(workerBase() + path, Object.assign({ cache: 'no-store', headers: authHeaders() }, opts || {}));
  let body = null; try { body = await res.json(); } catch (e) { body = null; }
  if (!res.ok) throw new Error((body && (body.error || body.message)) || ('HTTP ' + res.status));
  return body || {};
}

function box() { return document.getElementById('sfRolesBox'); }

function draw() {
  const host = box(); if (!host) return;
  const d = _data;
  if (!d) { host.innerHTML = '<div style="color:var(--sub);font-size:12px">Loading role history...</div>'; return; }
  if (!d.ready) { host.innerHTML = '<div style="color:var(--sub);font-size:12px">Promotions are not set up yet (database update v231-0).</div>'; return; }
  const now = d.now || {};
  const parts = [];
  if (now.acting) {
    parts.push('<div class="badge badge-amber" style="display:inline-block;margin-bottom:6px">Acting ' + esc(roleLabel(now.role)) + (now.areaId ? ' (' + esc(areaName(now.areaId)) + ')' : '') + ' until ' + esc(shortDate(now.until)) + '</div>');
  }
  (d.upcoming || []).forEach((p) => {
    parts.push('<div style="font-size:12px;color:var(--sub);margin-bottom:4px">Planned: ' + esc(describePeriod(p, areaName)) + '</div>');
  });
  const rows = (d.periods || []).map((p) => {
    const running = p.kind === 'acting' && !p.cancelled_at && p.ends_on >= d.today;
    const btns = [];
    if (d.headOffice && running) btns.push('<button type="button" class="btn btn-outline btn-sm" onclick="StaffRoles.end(' + Number(p.id) + ')">End now</button>');
    if (d.headOffice && !p.cancelled_at) btns.push('<button type="button" class="btn btn-outline btn-sm" style="color:var(--red);border-color:var(--red)" onclick="StaffRoles.cancel(' + Number(p.id) + ')">Mark as a mistake</button>');
    const who = p.set_by_name ? ' <span style="color:var(--sub)">by <span translate="no">' + esc(p.set_by_name) + '</span></span>' : '';
    const ended = p.ended_by_name ? ' <span style="color:var(--sub)">(ended early by <span translate="no">' + esc(p.ended_by_name) + '</span>)</span>' : '';
    const note = p.note ? '<div translate="no" style="color:var(--sub);font-size:11px">' + esc(p.note) + '</div>' : '';
    return '<li style="margin:4px 0;' + (p.cancelled_at ? 'opacity:.55' : '') + '"><div style="font-size:12px">' + esc(describePeriod(p, areaName)) + who + ended + '</div>' + note
      + (btns.length ? '<div style="display:flex;gap:6px;margin-top:4px">' + btns.join('') + '</div>' : '') + '</li>';
  });
  parts.push(rows.length ? '<ul style="margin:0;padding-inline-start:16px">' + rows.join('') + '</ul>' : '<div style="font-size:12px;color:var(--sub)">No promotions or acting roles recorded.</div>');
  if (d.headOffice) {
    parts.push('<button type="button" class="btn btn-outline btn-sm" style="margin-top:8px" onclick="StaffRoles.openForm()">Promote or act up</button><div id="srForm"></div>');
  }
  host.innerHTML = parts.join('');
}

export async function load(staffId) {
  _staffId = staffId; _data = null; draw();
  const forId = staffId;
  try {
    const d = await call('/api/admin/staff-roles?staff_id=' + encodeURIComponent(staffId));
    if (_staffId !== forId) return;   // the modal moved on to someone else
    _data = d; draw();
  } catch (e) {
    if (_staffId !== forId) return;
    const host = box(); if (host) host.innerHTML = '<div style="color:var(--sub);font-size:12px">Role history could not load: ' + esc(e.message) + '</div>';
  }
}

export function openForm() {
  const host = document.getElementById('srForm'); if (!host || !_data) return;
  const today = _data.today || '';
  const roleOpts = PROMOTION_ROLES.map((r) => '<option value="' + esc(r.value) + '">' + esc(r.label) + '</option>').join('');
  const areaOpts = '<option value="">Not an area role</option>' + areas().map((a) => '<option translate="no" value="' + esc(a.id) + '">' + esc(a.name) + '</option>').join('');
  host.innerHTML = '<div style="border:1px solid var(--border);border-radius:10px;padding:10px;margin-top:8px;display:grid;gap:8px">'
    + '<label style="font-size:12px">New role <select class="form-select" id="srRole">' + roleOpts + '</select></label>'
    + '<div style="font-size:12px;display:flex;gap:14px;flex-wrap:wrap">'
    + '<label><input type="radio" name="srKind" id="srKindPermanent" value="permanent" checked onchange="StaffRoles.kindChanged()"> Permanent promotion</label>'
    + '<label><input type="radio" name="srKind" id="srKindActing" value="acting" onchange="StaffRoles.kindChanged()"> Acting (temporary)</label></div>'
    + '<label style="font-size:12px">From <input type="date" class="form-input" id="srStart" value="' + esc(today) + '"></label>'
    + '<label style="font-size:12px;display:none" id="srEndWrap">Until (last day) <input type="date" class="form-input" id="srEnd"></label>'
    + '<label style="font-size:12px">Area <select class="form-select" id="srArea">' + areaOpts + '</select></label>'
    + '<label style="font-size:12px">Note (optional) <input type="text" class="form-input" id="srNote" maxlength="500" placeholder="e.g. covering annual leave"></label>'
    + '<div id="srHint" style="font-size:11px;color:var(--sub)">A permanent promotion changes their role from the date given (today or earlier). Their record, history and PIN stay the same.</div>'
    + '<div style="display:flex;gap:8px"><button type="button" class="btn btn-green btn-sm" id="srSave" onclick="StaffRoles.save()">Save</button>'
    + '<button type="button" class="btn btn-outline btn-sm" onclick="document.getElementById(\'srForm\').innerHTML=\'\'">Cancel</button></div></div>';
}

export function kindChanged() {
  const acting = document.getElementById('srKindActing') && document.getElementById('srKindActing').checked;
  const wrap = document.getElementById('srEndWrap'); if (wrap) wrap.style.display = acting ? '' : 'none';
  const hint = document.getElementById('srHint');
  if (hint) hint.textContent = acting
    ? 'An acting role shows on their record between the two dates and stops by itself. Their own role does not change. An acting area manager can sign in to admin on those days only if Head Office has given them an acting account (People, Accounts).'
    : 'A permanent promotion changes their role from the date given (today or earlier). Their record, history and PIN stay the same.';
}

export async function save() {
  if (_staffId == null) return;
  const v = (id) => { const el = document.getElementById(id); return el ? el.value : ''; };
  const kind = document.getElementById('srKindActing') && document.getElementById('srKindActing').checked ? 'acting' : 'permanent';
  const payload = { staff_id: _staffId, kind, role: v('srRole'), starts_on: v('srStart'), ends_on: kind === 'acting' ? v('srEnd') : null, area_id: v('srArea') || null, note: v('srNote') };
  const btn = document.getElementById('srSave'); if (btn) btn.disabled = true;
  try {
    const r = await call('/api/admin/staff-roles', { method: 'POST', body: JSON.stringify(payload) });
    if (kind === 'permanent') {
      // the form and the list follow the new role, so Save Changes does not see a stray role edit
      try {
        const s = (typeof staffData !== 'undefined' ? staffData : []).find((x) => x.id === _staffId);
        if (s) s.role = r.role;
        const sel = document.getElementById('sfRole');
        if (sel) {
          if (![...sel.options].some((o) => o.value === r.role)) { const o = document.createElement('option'); o.value = r.role; o.textContent = roleLabel(r.role); sel.appendChild(o); }
          sel.value = r.role;
        }
        if (typeof Uploads !== 'undefined' && Uploads.staff && Uploads.staff.renderStaff) Uploads.staff.renderStaff();
      } catch (e) { /* the list refreshes on the next load */ }
      toast('Promoted to ' + roleLabel(payload.role), 'success');
    } else {
      toast('Acting role saved', 'success');
      loadActing();
    }
    load(_staffId);
  } catch (e) {
    toast(e.message, 'error');
    if (btn) btn.disabled = false;
  }
}

async function patchPeriod(path, id, confirmText) {
  if (!confirm(confirmText)) return;
  try {
    const r = await call(path, { method: 'POST', body: JSON.stringify({ id }) });
    if (r.restoredRole) {
      const s = (typeof staffData !== 'undefined' ? staffData : []).find((x) => x.id === _staffId);
      if (s) s.role = r.restoredRole;
      const sel = document.getElementById('sfRole'); if (sel) sel.value = r.restoredRole;
      if (typeof Uploads !== 'undefined' && Uploads.staff && Uploads.staff.renderStaff) Uploads.staff.renderStaff();
    }
    toast(r.warning || 'Saved', r.warning ? 'error' : 'success');
    loadActing();
    load(_staffId);
  } catch (e) { toast(e.message, 'error'); }
}
export function end(id) { return patchPeriod('/api/admin/staff-roles/end', id, 'End this acting role now? It stays in their history.'); }
export function cancel(id) { return patchPeriod('/api/admin/staff-roles/cancel', id, 'Mark this as a mistake? It stays in the history, greyed out. A permanent promotion puts their previous role back.'); }

// The staff list chips: acting roles running now or planned. The staff table calls ensureActing() each
// time it draws; only the first call fetches (loadActing redraws the table when the answer arrives).
let _actingAsked = false;
export function ensureActing() {
  if (_actingAsked) return;
  _actingAsked = true;
  loadActing();
}
export async function loadActing() {
  try {
    const d = await call('/api/admin/staff-roles/acting');
    const next = {};
    (d.acting || []).forEach((a) => { next[a.staff_id] = a; });
    _acting = next;
    if (typeof Uploads !== 'undefined' && Uploads.staff && Uploads.staff.renderStaff) Uploads.staff.renderStaff();
  } catch (e) { /* no chips; the list still draws */ }
}
export function actingChip(staffId) {
  const a = _acting[staffId]; if (!a) return '';
  const text = a.running ? 'Acting ' + roleLabel(a.role) + ' until ' + shortDate(a.ends_on) : 'Acting ' + roleLabel(a.role) + ' from ' + shortDate(a.starts_on);
  return ' <span class="badge badge-amber" title="' + esc(shortDate(a.starts_on) + ' to ' + shortDate(a.ends_on)) + '">' + esc(text) + '</span>';
}
